export type BackgroundEngine = 'rx2go' | 'livekit'

// In-memory, per-session override. Used by the runtime auto-fallback so a real
// RVM failure forces 'livekit' for THIS session WITHOUT persisting to
// localStorage — otherwise a one-off failure would permanently brick the user's
// explicit 'rx2go' choice across reloads (it did; fixed 2026-06-13).
let sessionOverride: BackgroundEngine | null = null

/** Force an engine for the rest of this page session (not persisted).
 *  `null` clears the override (tests / QA). */
export function setBackgroundEngineSessionOverride(engine: BackgroundEngine | null): void {
  sessionOverride = engine
}

/**
 * Which background processor to use. Override order (first match wins):
 *   1. in-memory session override (runtime auto-fallback)               — not persisted
 *   2. localStorage 'rx2go.bgEngine' = 'rx2go' | 'livekit'              (per-device QA toggle)
 *   3. import.meta.env.VITE_BG_ENGINE                                   (build-time)
 *   4. 'rx2go'                                                          (default — RVM matting)
 *
 * Default is 'rx2go' (the RVM matting engine — OBS-tier soft edges) as of
 * 1.1.648A. It self-protects: if the model can't load OR inference repeatedly
 * errors, CombinedProcessor / the runtime fast-fall-back swap in the stock
 * livekit processor — so the worst case on any device is the old behaviour.
 * Global kill switch if needed: build with VITE_BG_ENGINE=livekit (one-line
 * redeploy), or a per-device override via localStorage 'rx2go.bgEngine'.
 *
 * A function (not a const) so a QA tester can flip localStorage and reopen the
 * picker without a rebuild.
 */
export function backgroundEngine(): BackgroundEngine {
  if (sessionOverride) return sessionOverride
  try {
    const ls = typeof localStorage !== 'undefined' ? localStorage.getItem('rx2go.bgEngine') : null
    if (ls === 'rx2go' || ls === 'livekit') return ls
  } catch {}
  const env = (import.meta as any)?.env?.VITE_BG_ENGINE
  if (env === 'rx2go' || env === 'livekit') return env
  return 'rx2go'
}
